import { Plan } from '@/generated/prisma/enums';

export type Feature =
  | 'recurring_invoices'
  | 'expenses'
  | 'ocr'
  | 'accountant_invites'
  | 'no_pdf_branding';

export interface PlanDefinition {
  plan: Plan;
  label: string;
  priceMonthly: number;
  priceYearly: number;
  limits: {
    invoicesPerMonth: number | null;
    ocrReceiptsPerMonth: number | null;
    ocrInvoicesPerMonth: number | null;
  };
  features: Feature[];
  stripePriceIds: {
    monthly: string | null;
    yearly: string | null;
  };
}

export const PLANS: Record<Plan, PlanDefinition> = {
  FREE: {
    plan: 'FREE',
    label: 'Free',
    priceMonthly: 0,
    priceYearly: 0,
    limits: {
      invoicesPerMonth: 3,
      ocrReceiptsPerMonth: 0,
      ocrInvoicesPerMonth: 0,
    },
    features: [],
    stripePriceIds: { monthly: null, yearly: null },
  },
  STARTER: {
    plan: 'STARTER',
    label: 'Starter',
    priceMonthly: 6.99,
    priceYearly: 64.99,
    limits: {
      invoicesPerMonth: 20,
      ocrReceiptsPerMonth: 0,
      ocrInvoicesPerMonth: 0,
    },
    features: ['recurring_invoices', 'no_pdf_branding'],
    stripePriceIds: {
      monthly: process.env.STRIPE_PRICE_STARTER_MONTHLY ?? null,
      yearly: process.env.STRIPE_PRICE_STARTER_YEARLY ?? null,
    },
  },
  PRO: {
    plan: 'PRO',
    label: 'Pro',
    priceMonthly: 14.99,
    priceYearly: 139.99,
    limits: {
      invoicesPerMonth: null,
      ocrReceiptsPerMonth: 50,
      ocrInvoicesPerMonth: 20,
    },
    features: [
      'recurring_invoices',
      'expenses',
      'ocr',
      'accountant_invites',
      'no_pdf_branding',
    ],
    stripePriceIds: {
      monthly: process.env.STRIPE_PRICE_PRO_MONTHLY ?? null,
      yearly: process.env.STRIPE_PRICE_PRO_YEARLY ?? null,
    },
  },
};

/**
 * User shape used by the gate helpers — accepts any object with the
 * subset of fields we need, so both session users and DB users work.
 */
export type PlanHolder = {
  plan?: Plan | null;
  grandfathered?: boolean | null;
};

/** Plan gate keyed on a user object. Handles nullish plan/grandfathered. */
export function userCan(
  user: PlanHolder | null | undefined,
  feature: Feature
): boolean {
  if (user?.grandfathered) return true;
  return PLANS[user?.plan ?? 'FREE'].features.includes(feature);
}

type LimitKey = keyof PlanDefinition['limits'];

function planLimit(
  user: PlanHolder | null | undefined,
  key: LimitKey
): number | null {
  if (user?.grandfathered) return null;
  return PLANS[user?.plan ?? 'FREE'].limits[key];
}

/** Monthly invoice limit. `null` = unlimited. */
export function userInvoiceLimit(user: PlanHolder | null | undefined) {
  return planLimit(user, 'invoicesPerMonth');
}

/** Monthly receipt-OCR limit. `null` = unlimited, `0` = feature off. */
export function userOcrReceiptLimit(user: PlanHolder | null | undefined) {
  return planLimit(user, 'ocrReceiptsPerMonth');
}

/** Monthly invoice-OCR limit. `null` = unlimited, `0` = feature off. */
export function userOcrInvoiceLimit(user: PlanHolder | null | undefined) {
  return planLimit(user, 'ocrInvoicesPerMonth');
}

/**
 * Single source of truth for gate error messages. Every server-action gate
 * should surface one of these strings so the UI can render consistent,
 * localized upgrade prompts.
 */
export function featureGateError(feature: Feature): string {
  switch (feature) {
    case 'expenses':
      return 'Expense tracking requires the Pro plan. Upgrade at /pricing.';
    case 'ocr':
      return 'AI receipt scanning requires the Pro plan. Upgrade at /pricing.';
    case 'accountant_invites':
      return 'Accountant access requires the Pro plan. Upgrade at /pricing.';
    case 'recurring_invoices':
      return 'Recurring invoices require the Starter or Pro plan. Upgrade at /pricing.';
    case 'no_pdf_branding':
      return 'This feature requires a paid plan. Upgrade at /pricing.';
  }
}

/**
 * True when transitioning `from` → `to` unlocks a feature the old plan
 * didn't have. Used to decide whether to flip defaults like `enableExpenses`
 * on upgrade (FREE/STARTER → PRO) without re-triggering on renewals.
 */
export function planGrantedFeature(
  from: Plan,
  to: Plan,
  feature: Feature
): boolean {
  return (
    !PLANS[from].features.includes(feature) &&
    PLANS[to].features.includes(feature)
  );
}

export function resolvePriceId(
  priceId: string
): { plan: Plan; interval: 'month' | 'year' } | null {
  for (const def of Object.values(PLANS)) {
    if (def.stripePriceIds.monthly === priceId) {
      return { plan: def.plan, interval: 'month' };
    }
    if (def.stripePriceIds.yearly === priceId) {
      return { plan: def.plan, interval: 'year' };
    }
  }
  return null;
}
